/**
 * Single source of truth for the keyboard-shortcut combo format.
 *
 * Page shortcuts register their combo as a structured object (`KeyCombo`) so the type
 * system rules out malformed strings. `useKeyboardShortcuts` builds the same shape from
 * each `keydown` event for matching; `KeyboardShortcutsOverlay` consumes the same shape
 * to render key pills. Keep producers and consumers going through these helpers so the
 * format stays in lockstep.
 */

/** Structured representation of a keyboard combo. `hasShift` defaults to `false`. */
export type KeyCombo = { key: string; hasShift?: boolean }

/**
 * Build the canonical combo from a `keydown` event.
 *
 * `hasShift` is only set when the resulting `key` is a letter (a–z). Rationale: on
 * AZERTY (French) keyboards, digits require Shift to type — pressing the `1` key
 * produces `event.key = '1', shiftKey = true`. Treating that as `shift+1` would never
 * match a registered `'1'` combo, breaking digit shortcuts for AZERTY users. For
 * non-letter keys, the resulting character already encodes the user's intent
 * (Shift+1 → `'1'` on AZERTY, `'!'` on QWERTY), so we don't add a redundant flag.
 */
export const buildCombo = (event: KeyboardEvent): KeyCombo => {
  const key = event.key.toLowerCase()
  const isLetter = /^[a-z]$/.test(key)
  return event.shiftKey && isLetter ? { key, hasShift: true } : { key }
}

/** Two combos match when both `key` and effective `hasShift` agree. */
export const combosMatch = (a: KeyCombo, b: KeyCombo): boolean =>
  a.key === b.key && Boolean(a.hasShift) === Boolean(b.hasShift)

/**
 * Canonical stable string form for a combo (`'shift+r'`, `'1'`). Used as a React `key`
 * prop when rendering combo lists and as the analytics dimension when emitting telemetry.
 * Not used for matching — that goes through `combosMatch` to keep the structured form
 * authoritative.
 */
export const comboToString = (combo: KeyCombo): string =>
  combo.hasShift ? `shift+${combo.key}` : combo.key
